<script lang="ts">
  import { getContext } from 'svelte';
  import newUniqueId from 'locally-unique-id-generator';
  import { type SanityContext, key } from '$lib/context/sanity';
  import type { NewsCardImage } from '$lib/sanity/schemas';
  import { toCSSVars } from '$lib/utils/cssVars';
  import { clipPaths } from './ClipPaths';
  import {
    toImageSrcAttributes,
    toLazyloadSrcsetUrlBuilders,
    toSrcsetUrlBuilders,
    useSanityImageLazyload
  } from '$lib/sanity/image';
  import { pipe } from '$lib/utils/pipe';
  import { ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { cx } from '@emotion/css';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';

  // svelte-ignore non_reactive_update
  enum Animation {
    Scale,
    Rotate,
    ChangeBorderAndScale
  }

  interface Props {
    image: NewsCardImage;
    animate: boolean;
    sourceMap: ContentSourceMap | undefined;
    path?: StudioPathLike;
  }

  let { image, animate, sourceMap, path = [] }: Props = $props();

  const { urlBuilder } = getContext<SanityContext>(key);
  const { isInView, lazyload } = useSanityImageLazyload();

  function ratioCSSVar(aspectRatio: string | undefined) {
    if (!aspectRatio) {
      return '1 / 1';
    }

    return aspectRatio.replace(':', ' / ');
  }

  function imageMaskPathFor(image: NewsCardImage) {
    if (!image.aspectRatio || image.aspectRatio === '1:1' || image.imageMask === 'square') {
      return null;
    }

    const ratio = image.aspectRatio.replace(':', 'x');
    return `/images/news/masks/${image.imageMask}${ratio}.svg`;
  }

  function animationFor(image: NewsCardImage) {
    if (image.imageMask === 'square') {
      return Animation.ChangeBorderAndScale;
    } else if (image.aspectRatio === '4:5' || image.aspectRatio === '5:4') {
      return Animation.Scale;
    } else {
      return Animation.Rotate;
    }
  }

  function imageClipFor(image: NewsCardImage) {
    if (!image.imageMask || image.aspectRatio !== '1:1') {
      return null;
    }

    return clipPaths[image.imageMask];
  }

  let encodeDataAttribute = $derived(useEncodeDataAttribute(image, sourceMap, sanityStudioURL));
  let aspectRatio = $derived(image.aspectRatio && ratioCSSVar(image.aspectRatio));
  let aspectWidth = $derived(image.aspectRatio?.[0]);
  let aspectHeight = $derived(image.aspectRatio?.[2]);
  let animation = $derived(image.image && animationFor(image));
  let mask = $derived(image.imageMask && imageMaskPathFor(image));
  let clip = $derived(image.imageMask && imageClipFor(image));
  let clipPathId = $derived(`image-clip-${newUniqueId()}`);
  let imageSrcAttrs = $derived(
    image?.image?.asset &&
      pipe(
        image.image?.asset,
        toSrcsetUrlBuilders(urlBuilder),
        toLazyloadSrcsetUrlBuilders($isInView),
        toImageSrcAttributes()
      )
  );

  const responsiveVars = new ResponsiveVars()
    .addFluid('square-border-radius', toFluidVarInput(30, 30))
    .addFluid('square-fallback-border-radius', toFluidVarInput(24, 30))
    .addFluid('border-and-scale-radius', toFluidVarInput(45, 45))
    .addFluid('border-and-scale-fallback-radius', toFluidVarInput(48, 60))
    .toCSS();
</script>

<div
  class={cx(responsiveVars, 'image-container')}
  class:animate
  class:scale={animation === Animation.Scale}
  class:rotate={animation === Animation.Rotate}
  class:border-and-scale={animation === Animation.ChangeBorderAndScale}
  class:with-clip={!!clip}
  class:with-mask={!!mask}
  class:with-aspect-ratio={aspectRatio}
  class:square={image.imageMask === 'square'}
  style={toCSSVars({
    aspectRatio,
    aspectWidth,
    aspectHeight,
    maskPath: `url('${mask}')`,
    clipPath: `url(#${clipPathId})`
  })}
  data-sanity={encodeDataAttribute([...path, 'image', 'imageRef'])}
>
  {#if imageSrcAttrs}
    <img
      use:lazyload
      class="lazy"
      src={imageSrcAttrs.src}
      srcset={imageSrcAttrs.srcset}
      sizes="100vw"
      class:with-clip={image.imageMask && image.imageMask !== 'square'}
      class:with-aspect-ratio={image.aspectRatio}
      alt={image?.image?.asset?.altText || ''}
    />
  {/if}

  {#if clip}
    <svg width="0" height="0" class="clip">
      <defs>
        <clipPath
          transform={`scale(${1 / clip.width}, ${1 / clip.height})`}
          id={clipPathId}
          clipPathUnits="objectBoundingBox"
        >
          <path class={`${image.imageMask} clip-path`} d={clip.clipPath} />
        </clipPath>
      </defs>
    </svg>
  {/if}
</div>

<style lang="scss">
  @import 'src/scss/accessibility';
  @import 'src/scss/media';
  @import 'src/scss/lazyload';

  .image-container {
    &.with-aspect-ratio {
      aspect-ratio: var(--aspect-ratio);
    }

    &.with-mask {
      mask-image: var(--mask-path);
      mask-size: contain;
      mask-repeat: no-repeat;
      mask-position: center;
      -webkit-mask-image: var(--mask-path);
      -webkit-mask-size: contain;
      -webkit-mask-repeat: no-repeat;
      -webkit-mask-position: center;

      img {
        transition: transform 0.2s ease-out;
        -webkit-transition: transform 0.2s ease-out;
        will-change: transform;
      }
    }

    &.square {
      overflow: hidden;
      border-radius: var(--square-border-radius);
      transition: border-radius 0.2s ease-out;
      -webkit-transition: border-radius 0.2s ease-out;
      will-change: transform;

      img {
        transition: transform 0.2s ease-out;
        -webkit-transition: transform 0.2s ease-out;
      }
    }

    &.with-clip {
      img {
        clip-path: var(--clip-path);
        -webkit-clip-path: var(--clip-path);
      }
    }
  }

  img {
    @include lazyload-blur();

    width: 100%;
    max-height: 100%;

    &.with-aspect-ratio {
      aspect-ratio: var(--aspect-ratio);
      object-fit: cover;
    }
  }

  .clip-path {
    transform: rotate(0deg);
    transform-box: fill-box;
    transform-origin: 50% 50%;
    transition: transform 0.2s ease-out;
  }

  @include without-reduce-motion {
    @media (hover: hover) {
      .animate {
        &.scale img {
          transform: scale(1.03);
        }

        @supports (aspect-ratio: var(--aspect-ratio)) {
          &.border-and-scale {
            border-radius: var(--border-and-scale-radius);

            img {
              transform: scale(1.03);
            }
          }
        }

        &.rotate {
          .clip-path {
            transform: rotate(-90deg);
            -moz-transform: rotate(-90deg) translateX(-15%);

            &.clover {
              transform: rotate(-45deg);
            }

            &.hexagon {
              transform: rotate(-30deg);
            }

            &.oval {
              transform: rotate(-90deg);
              -moz-transform: rotate(90deg) translate(7%, -7%);
            }
          }
        }
      }
    }
  }

  .clip {
    position: absolute;
  }

  @supports not (aspect-ratio: var(--aspect-ratio)) {
    .image-container {
      width: 100%;
      height: auto;
      padding-top: calc(100% * calc(var(--aspect-height) / var(--aspect-width)));

      &.square {
        img {
          border-radius: var(--square-fallback-border-radius);
          transition: transform 0.2s ease-out;
          -webkit-transition: transform 0.2s ease-out;
        }
      }
    }

    img {
      height: 100%;
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
    }

    @include without-reduce-motion {
      @media (hover: hover) {
        .animate.border-and-scale {
          img {
            border-radius: var(--border-and-scale-fallback-radius);
          }
        }
      }
    }
  }
</style>
